// ============================================================================
// TRAINING LOBBY
// ----------------------------------------------------------------------------
// One screen, one obvious thing to do. Built for phones first and for
// players who do not live in poker apps:
//   1. Play now      - one big button, sensible defaults, settings folded away
//   2. Activities    - six large tiles, one line each, no jargon
//   3. Your progress - coins, streak, level and rewards in a single strip
//   4. Recent        - the last three sessions; the full list lives one tap
//                      away and loads five at a time instead of fifty
// Everything the old lobby did (create/join tables, reports, retention,
// progress analytics, Poker 101, beginner wording) is still here, just
// arranged so it can be read at arm's length.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, VStack, HStack, Box, Text, Heading, Grid, Progress, Input, Badge,
  useToast, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure, Checkbox, Switch, Button, IconButton,
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Trophy, Target, TrendingUp, Users, Zap, MessageCircle, BookOpen, Bot, Brain,
  Trash2, AlertTriangle, Coins, Flame, Gift, Star, ChevronRight, ChevronDown, ChevronUp,
  GraduationCap, Grid3x3, Play,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSkillLevel } from '../contexts/SkillLevelContext';
import { POKER_COLORS } from '../theme/designSystem';
import PokerButton from '../components/ui/PokerButton';
import DailyRewardsWidget from '../components/training/DailyRewardsWidget';
import Poker101Modal from '../components/Poker101Modal';
import {
  createTrainingTable,
  getUserTrainingTables,
  closeTrainingTables,
  joinTrainingTable,
  getProgressAnalytics,
  getUserTrainingReports,
  deleteTrainingReport,
  deleteMultipleReports,
  getRetentionStatus,
  type TrainingTable,
  type TrainingProgress,
  type ReportSummary,
  type BotDifficulty,
} from '../services/training';
import geoService from '../services/geoService';

const triggerHaptic = (type: 'light' | 'medium' | 'success' = 'light') => {
  if ('vibrate' in navigator) {
    const patterns = { light: [10], medium: [25], success: [10, 50, 20] };
    navigator.vibrate(patterns[type]);
  }
};

const CARD = '#0f1722';
const BORDER = 'rgba(148,163,184,0.18)';
const HISTORY_PAGE = 5;

type View = 'home' | 'history' | 'progress';

// A large, thumb-sized choice pill used in the settings
const Choice: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <Box as="button" minH="46px" px={4} borderRadius="full" fontSize="md" fontWeight="700"
    bg={active ? POKER_COLORS.cyan : 'whiteAlpha.100'} color={active ? 'black' : 'gray.200'}
    border="1px solid" borderColor={active ? 'transparent' : 'whiteAlpha.200'}
    onClick={() => { triggerHaptic('light'); onClick(); }} _active={{ transform: 'scale(0.97)' }} transition="all 0.12s">
    {children}
  </Box>
);

// One activity tile: icon, title, one line, chevron. Whole tile is the button.
const Tile: React.FC<{ icon: React.ReactNode; color: string; title: string; text: string; tag?: string; onClick: () => void; tutorial?: string }> =
  ({ icon, color, title, text, tag, onClick, tutorial }) => (
  <Box as="button" data-tutorial={tutorial} onClick={() => { triggerHaptic('light'); onClick(); }} textAlign="left" w="100%"
    bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" p={4} minH="84px"
    display="flex" alignItems="center" gap={4}
    _hover={{ borderColor: color, transform: 'translateY(-1px)' }} _active={{ transform: 'scale(0.99)' }} transition="all 0.15s">
    <Box bg={`${color}22`} color={color} p={3} borderRadius="xl" flexShrink={0}>{icon}</Box>
    <Box flex={1} minW={0}>
      <HStack spacing={2}>
        <Text color="white" fontWeight="800" fontSize="lg" lineHeight="1.2">{title}</Text>
        {tag && <Badge bg={`${color}33`} color={color} fontSize="10px" borderRadius="full" px={2}>{tag}</Badge>}
      </HStack>
      <Text color="gray.400" fontSize="md" noOfLines={2} mt={0.5}>{text}</Text>
    </Box>
    <Box color="gray.500" flexShrink={0}><ChevronRight size={22} /></Box>
  </Box>
);

const TrainingLobby: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { progress: skillProgress, canClaimDaily, canClaimWeekly } = useSkillLevel();
  const navigate = useNavigate();
  const toast = useToast();

  // ── State ──
  const [view, setView] = useState<View>('home');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [botCount, setBotCount] = useState(3);
  const [botDifficulty, setBotDifficulty] = useState<BotDifficulty | 'mixed'>('medium');
  const [blinds, setBlinds] = useState<'1/2' | '2/5' | '5/10'>('1/2');
  const [startingStack, setStartingStack] = useState(200);
  const [showSettings, setShowSettings] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [showRewards, setShowRewards] = useState(false);

  const [joinCode, setJoinCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [myTables, setMyTables] = useState<TrainingTable[]>([]);
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [progress, setProgress] = useState<TrainingProgress | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [historyShown, setHistoryShown] = useState(HISTORY_PAGE);

  const [retentionStatus, setRetentionStatus] = useState<{
    totalReports: number; maxReports: number; isAtLimit: boolean; isNearLimit: boolean; recommendedAction: string | null;
  } | null>(null);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedReports, setSelectedReports] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const [beginnerMode, setBeginnerMode] = useState(() => {
    const saved = localStorage.getItem('poker_beginner_mode');
    return saved !== null ? saved === 'true' : true;
  });
  const [showPoker101, setShowPoker101] = useState(false);
  const [isRestrictedState, setIsRestrictedState] = useState(false);
  const poker101Done = !!localStorage.getItem('poker101_completed');

  // Friend-invite deep link: /training?join=CODE
  const [searchParams] = useSearchParams();
  const autoJoinTriedRef = React.useRef(false);
  useEffect(() => {
    const code = searchParams.get('join');
    if (!code || autoJoinTriedRef.current || !currentUser?.uid) return;
    autoJoinTriedRef.current = true;
    (async () => {
      const username = userProfile?.username || (userProfile as any)?.displayName || 'Player';
      const result = await joinTrainingTable(code.toUpperCase(), currentUser.uid, username);
      if (result.success && result.table) navigate(`/training/${result.table.tableId}`);
      else setError(result.error || 'That invite is no longer valid');
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentUser?.uid]);

  useEffect(() => {
    const checkGeo = async () => {
      if (!currentUser?.uid) return;
      try {
        const geoResult = await geoService.checkLocation();
        setIsRestrictedState(geoResult.canPlayPKCash === false && !geoResult.adminBypassed);
      } catch (e) { console.error('Geo check failed:', e); }
    };
    checkGeo();
  }, [currentUser?.uid]);

  useEffect(() => { if (currentUser?.uid) loadUserData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);
  useEffect(() => { localStorage.setItem('poker_beginner_mode', String(beginnerMode)); }, [beginnerMode]);

  const loadUserData = async () => {
    if (!currentUser?.uid) return;
    try {
      const tables = await getUserTrainingTables(currentUser.uid);
      setMyTables(tables.filter(t => t.status !== 'ended'));
      const userReports = await getUserTrainingReports(currentUser.uid, 50);
      setReports(userReports);
      const analyticsData = await getProgressAnalytics(currentUser.uid);
      setProgress(analyticsData.progress);
      setAnalytics(analyticsData);
      try { setRetentionStatus(await getRetentionStatus()); } catch (e) { console.warn('Could not load retention status:', e); }
    } catch (err) {
      console.error('Failed to load training data:', err);
    }
  };

  // ── Handlers ──
  const handlePoker101Complete = useCallback(() => {
    setShowPoker101(false);
    localStorage.setItem('poker101_completed', 'true');
    toast({ title: 'Poker 101 complete', description: 'You are ready to practice. Good luck!', status: 'success', duration: 4000 });
  }, [toast]);

  const handleCreateTable = async () => {
    const username = userProfile?.username || (userProfile as any)?.displayName || 'Player';
    if (!currentUser?.uid) { setError('Please log in to start a practice game'); return; }
    setIsLoading(true);
    setError(null);
    try {
      const blindValues = { '1/2': { small: 1, big: 2 }, '2/5': { small: 2, big: 5 }, '5/10': { small: 5, big: 10 } };
      // Tidy up first: solo tables this player hosted and walked away from
      // would otherwise pile up as "still open" forever. Tables with other
      // humans seated are left alone.
      const stale = myTables.filter(t => t.hostId === currentUser.uid && t.players.filter(p => p !== currentUser.uid).length === 0);
      if (stale.length > 0) {
        await closeTrainingTables(currentUser.uid, stale);
        setMyTables(prev => prev.filter(t => !stale.some(s => s.tableId === t.tableId)));
      }
      const table = await createTrainingTable({
        tableName: `${username}'s Training`,
        hostId: currentUser.uid, hostName: username,
        smallBlind: blindValues[blinds].small, bigBlind: blindValues[blinds].big,
        startingStack, botCount, botDifficulty, maxPlayers: 8, autoStart: false,
      });
      navigate(`/training/${table.tableId}`);
    } catch (err) {
      console.error('Failed to create training table:', err);
      setError('Could not start the practice game. Please try again.');
    } finally { setIsLoading(false); }
  };

  const handleJoinTable = async () => {
    const username = userProfile?.username || (userProfile as any)?.displayName || 'Player';
    if (!currentUser?.uid) { setError('Please log in to join'); return; }
    if (!joinCode.trim()) { setError('Enter the code your friend gave you'); return; }
    setIsJoining(true);
    setError(null);
    try {
      const result = await joinTrainingTable(joinCode.trim().toUpperCase(), currentUser.uid, username);
      if (result.success && result.table) navigate(`/training/${result.table.tableId}`);
      else setError(result.error || 'Could not join that table');
    } catch { setError('Could not join that table'); }
    finally { setIsJoining(false); }
  };

  const toggleReportSelection = (reportId: string) => {
    setSelectedReports(prev => { const n = new Set(prev); if (n.has(reportId)) n.delete(reportId); else n.add(reportId); return n; });
  };
  const handleDeleteSelected = async () => {
    if (selectedReports.size === 0) return;
    setIsDeleting(true);
    try {
      const result = await deleteMultipleReports(Array.from(selectedReports));
      if (result.success || result.deleted > 0) {
        toast({ title: `Deleted ${result.deleted} session${result.deleted === 1 ? '' : 's'}`, status: 'success', duration: 3000 });
        setSelectedReports(new Set());
        setSelectMode(false);
        await loadUserData();
      } else {
        toast({ title: 'Could not delete', description: result.errors?.join(', '), status: 'error', duration: 5000 });
      }
    } catch { toast({ title: 'Could not delete', status: 'error', duration: 3000 }); }
    finally { setIsDeleting(false); onDeleteClose(); }
  };
  const handleDeleteSingle = async (reportId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this session report?')) return;
    setIsDeleting(true);
    try {
      const result = await deleteTrainingReport(reportId);
      if (result.success) { toast({ title: 'Session deleted', status: 'success', duration: 2000 }); await loadUserData(); }
      else toast({ title: 'Could not delete', description: result.error, status: 'error', duration: 3000 });
    } catch { toast({ title: 'Could not delete', status: 'error', duration: 3000 }); }
    finally { setIsDeleting(false); }
  };

  const [isClosingTables, setIsClosingTables] = useState(false);
  const handleCloseTables = async (tables: TrainingTable[]) => {
    if (!currentUser?.uid || tables.length === 0) return;
    setIsClosingTables(true);
    try {
      const result = await closeTrainingTables(currentUser.uid, tables);
      setMyTables(prev => prev.filter(t => !tables.some(s => s.tableId === t.tableId)));
      toast({ title: result.closed === 1 ? 'Game closed' : `${result.closed} games closed`, status: 'success', duration: 2500 });
    } catch { toast({ title: 'Could not close those games', status: 'error', duration: 3000 }); }
    finally { setIsClosingTables(false); }
  };
  // Newest first, so "Rejoin" always goes to the game you were just in
  const openTables = useMemo(() => [...myTables].sort((a, b) => {
    const ta = (a.updatedAt as any)?.toMillis?.() ?? (a.createdAt as any)?.toMillis?.() ?? 0;
    const tb = (b.updatedAt as any)?.toMillis?.() ?? (b.createdAt as any)?.toMillis?.() ?? 0;
    return tb - ta;
  }), [myTables]);

  const scoreColor = (score: number) => score >= 8 ? '#22c55e' : score >= 6 ? POKER_COLORS.gold : '#f87171';
  const fmtDate = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const stackLabel = beginnerMode ? `$${startingStack * 2}` : `${startingStack} BB`;
  const settingsSummary = `${botCount} opponent${botCount === 1 ? '' : 's'} · ${botDifficulty === 'mixed' ? 'Mixed skill' : `${botDifficulty[0].toUpperCase()}${botDifficulty.slice(1)} skill`} · $${blinds} blinds · ${stackLabel} to start`;
  const coinsLabel = isRestrictedState ? 'PlayCoins earned' : 'PSS Coins earned';
  const coinsValue = isRestrictedState
    ? Math.floor((skillProgress?.pkCashEarned || 0) * 100).toLocaleString()
    : `$${(skillProgress?.pkCashEarned || 0).toFixed(2)}`;
  const xpPct = Math.min(100, ((skillProgress?.xp || 0) / (skillProgress?.xpToNextLevel || 1000)) * 100);
  const recent = useMemo(() => reports.slice(0, 3), [reports]);

  // ── Shared pieces ──
  const viewSwitch = (
    <HStack bg={CARD} borderRadius="full" p={1} spacing={1} border="1px solid" borderColor={BORDER}>
      {([['home', 'Home', <Zap size={16} />], ['history', 'Sessions', <Trophy size={16} />], ['progress', 'Progress', <TrendingUp size={16} />]] as Array<[View, string, React.ReactNode]>).map(([id, label, icon]) => (
        <Button key={id} flex={1} minH="44px" borderRadius="full" size="sm" fontSize="md"
          variant={view === id ? 'solid' : 'ghost'} bg={view === id ? POKER_COLORS.cyan : 'transparent'} color={view === id ? 'black' : 'gray.300'}
          _hover={{ bg: view === id ? POKER_COLORS.cyan : 'whiteAlpha.100' }} leftIcon={icon as any}
          onClick={() => { triggerHaptic('light'); setView(id); }}>
          {label}
        </Button>
      ))}
    </HStack>
  );

  const reportCard = (report: ReportSummary, compact = false) => (
    <Box key={report.reportId} as={selectMode ? 'div' : 'button'} textAlign="left" w="100%"
      onClick={() => !selectMode && navigate(`/training/report/${report.reportId}`)}
      bg={CARD} border="1px solid" borderColor={selectedReports.has(report.reportId) ? POKER_COLORS.cyan : BORDER} borderRadius="xl" p={4}
      _hover={{ borderColor: POKER_COLORS.cyan }} transition="all 0.15s">
      <HStack spacing={3}>
        {selectMode && <Checkbox isChecked={selectedReports.has(report.reportId)} onChange={() => toggleReportSelection(report.reportId)} colorScheme="cyan" size="lg" />}
        <Box textAlign="center" minW="56px">
          <Text fontSize="2xl" fontWeight="900" color={scoreColor(report.overallScore)} lineHeight={1}>{report.overallScore.toFixed(1)}</Text>
          <Text color="gray.500" fontSize="xs">out of 10</Text>
        </Box>
        <Box flex={1} minW={0}>
          <Text color="white" fontWeight="700" fontSize="md">{fmtDate(report.generatedAt)} · {report.handsPlayed} hands · {Math.max(1, Math.round(report.duration / 60000))} min</Text>
          {!compact && (
            <HStack mt={1} spacing={2} flexWrap="wrap">
              {report.topStrength && <Badge bg="rgba(34,197,94,0.18)" color="#4ade80" borderRadius="full" px={2} textTransform="none" fontSize="xs">Strong: {report.topStrength.split(':')[0]}</Badge>}
              {report.topWeakness && <Badge bg="rgba(248,113,113,0.18)" color="#fca5a5" borderRadius="full" px={2} textTransform="none" fontSize="xs">Work on: {report.topWeakness.split(':')[0]}</Badge>}
            </HStack>
          )}
          {compact && report.topWeakness && <Text color="gray.400" fontSize="sm" noOfLines={1}>Work on: {report.topWeakness.split(':')[0]}</Text>}
        </Box>
        {!selectMode && !compact && (
          <IconButton aria-label="Delete session" size="sm" variant="ghost" color="gray.500" _hover={{ color: 'red.300', bg: 'red.900' }} icon={<Trash2 size={16} />} isDisabled={isDeleting} onClick={(e) => handleDeleteSingle(report.reportId, e)} />
        )}
        {!selectMode && <Box color="gray.500"><ChevronRight size={20} /></Box>}
      </HStack>
    </Box>
  );

  // ── Render ──
  return (
    <Container maxW="3xl" py={{ base: 3, md: 6 }} px={{ base: 3, md: 6 }}
      css={{ paddingTop: 'max(env(safe-area-inset-top), 12px)', paddingBottom: 'max(env(safe-area-inset-bottom), 96px)' }}>
      <Poker101Modal isOpen={showPoker101} onClose={() => setShowPoker101(false)} onComplete={handlePoker101Complete} />

      <VStack spacing={5} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center" data-tutorial="training-header">
          <Box>
            <Heading size="lg" color="white">Training</Heading>
            <Text color="gray.400" fontSize="md">Practice for free. Nothing at risk.</Text>
          </Box>
          <HStack spacing={2} bg={CARD} px={3} py={2} borderRadius="full" border="1px solid" borderColor={BORDER}>
            <Text color={beginnerMode ? POKER_COLORS.cyan : 'gray.400'} fontSize="sm" fontWeight="700">{beginnerMode ? 'Simple words' : 'Poker terms'}</Text>
            <Switch size="md" isChecked={beginnerMode} onChange={() => setBeginnerMode(v => !v)} colorScheme="cyan" />
          </HStack>
        </HStack>

        {viewSwitch}

        {view === 'home' && (
          <>
            {/* Rejoin: one card however many games were left open */}
            {openTables.length > 0 && (() => {
              const latest = openTables[0];
              const others = openTables.slice(1);
              return (
                <Box bg="rgba(34,197,94,0.1)" border="1px solid rgba(34,197,94,0.45)" borderRadius="2xl" p={4}>
                  <HStack justify="space-between" align="flex-start" spacing={3}>
                    <Box flex={1} minW={0}>
                      <Text color="white" fontWeight="800" fontSize="md">
                        {openTables.length === 1 ? 'Your game is still open' : `You have ${openTables.length} games open`}
                      </Text>
                      <Text color="gray.300" fontSize="sm" noOfLines={1}>
                        {latest.tableName} · {latest.players.length} player{latest.players.length === 1 ? '' : 's'} + {latest.bots.length} computer{latest.bots.length === 1 ? '' : 's'}
                        {others.length > 0 ? ` · and ${others.length} older` : ''}
                      </Text>
                    </Box>
                    <Button minH="46px" colorScheme="green" fontSize="md" flexShrink={0} onClick={() => navigate(`/training/${latest.tableId}`)}>Rejoin</Button>
                  </HStack>
                  <HStack mt={3} spacing={4}>
                    {others.length > 0 && (
                      <Button size="sm" variant="link" color="gray.300" fontSize="sm" isLoading={isClosingTables} onClick={() => handleCloseTables(others)}>
                        Close the {others.length} older game{others.length === 1 ? '' : 's'}
                      </Button>
                    )}
                    <Button size="sm" variant="link" color="gray.400" fontSize="sm" isLoading={isClosingTables} onClick={() => handleCloseTables(openTables)}>
                      {openTables.length === 1 ? 'Close it' : 'Close all'}
                    </Button>
                  </HStack>
                </Box>
              );
            })()}

            {/* Play now: three ways in, biggest first */}
            <Box bg={`linear-gradient(135deg, ${POKER_COLORS.darkTeal} 0%, #101a33 100%)`} border="1px solid" borderColor={`${POKER_COLORS.cyan}55`} borderRadius="2xl" p={{ base: 4, md: 5 }} data-tutorial="training-quickstart">
              <HStack spacing={3} mb={3}>
                <Box bg={`${POKER_COLORS.cyan}22`} color={POKER_COLORS.cyan} p={3} borderRadius="xl"><Play size={24} /></Box>
                <Box>
                  <Text color="white" fontWeight="900" fontSize="xl" lineHeight="1.15">Play now</Text>
                  <Text color="gray.300" fontSize="md">Pick how you want to practice. Nothing at risk.</Text>
                </Box>
              </HStack>
              <PokerButton variant="primary" w="100%" h="60px" fontSize="lg" isLoading={isLoading} onClick={handleCreateTable} loadingText="Setting up your table">
                Practice table
              </PokerButton>
              <Text color="gray.400" fontSize="sm" mt={1.5} mb={3}>Full hands against the computer, with a report after.</Text>
              <Grid templateColumns="1fr 1fr" gap={3}>
                <Button minH="56px" bg="whiteAlpha.100" color="white" border="1px solid" borderColor="whiteAlpha.300" _hover={{ bg: 'whiteAlpha.200', borderColor: POKER_COLORS.gold }}
                  fontSize="md" fontWeight="800" whiteSpace="normal" leftIcon={<Brain size={20} color={POKER_COLORS.gold} />} onClick={() => { triggerHaptic('light'); navigate('/training/scenario'); }}>
                  Scenario
                </Button>
                <Button minH="56px" bg="whiteAlpha.100" color="white" border="1px solid" borderColor="whiteAlpha.300" _hover={{ bg: 'whiteAlpha.200', borderColor: '#f59e0b' }}
                  fontSize="md" fontWeight="800" whiteSpace="normal" leftIcon={<Trophy size={20} color="#f59e0b" />} onClick={() => { triggerHaptic('light'); navigate('/training/tournament'); }}>
                  Tournament
                </Button>
              </Grid>
              <HStack justify="space-between" mt={2} fontSize="xs" color="gray.500" px={1}>
                <Text flex={1}>One tough spot, then feedback.</Text>
                <Text flex={1} textAlign="right">Bubble, ICM and final-table drills.</Text>
              </HStack>
              <HStack justify="space-between" mt={3} pt={3} borderTop="1px solid" borderColor="whiteAlpha.200" flexWrap="wrap" gap={2}>
                <Text color="gray.300" fontSize="sm">Practice table: {settingsSummary}</Text>
                <Button size="sm" variant="link" color={POKER_COLORS.cyan} fontSize="md" rightIcon={showSettings ? <ChevronUp size={16} /> : <ChevronDown size={16} />} onClick={() => setShowSettings(v => !v)}>
                  {showSettings ? 'Hide settings' : 'Change settings'}
                </Button>
              </HStack>
              {showSettings && (
                <VStack align="stretch" spacing={4} mt={4} pt={4} borderTop="1px solid" borderColor="whiteAlpha.200">
                  <Box>
                    <Text color="gray.300" fontSize="md" fontWeight="700" mb={2}>{beginnerMode ? 'How good are the computer players?' : 'Bot difficulty'}</Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {(['easy', 'medium', 'hard', 'mixed'] as const).map(d => <Choice key={d} active={botDifficulty === d} onClick={() => setBotDifficulty(d)}>{d[0].toUpperCase() + d.slice(1)}</Choice>)}
                    </HStack>
                  </Box>
                  <Box>
                    <Text color="gray.300" fontSize="md" fontWeight="700" mb={2}>How many opponents?</Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {[1, 2, 3, 5, 7].map(n => <Choice key={n} active={botCount === n} onClick={() => setBotCount(n)}>{n}</Choice>)}
                    </HStack>
                  </Box>
                  <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={4}>
                    <Box>
                      <Text color="gray.300" fontSize="md" fontWeight="700" mb={2}>{beginnerMode ? 'Bet sizes' : 'Blinds'}</Text>
                      <HStack spacing={2} flexWrap="wrap">
                        {(['1/2', '2/5', '5/10'] as const).map(b => <Choice key={b} active={blinds === b} onClick={() => setBlinds(b)}>${b}</Choice>)}
                      </HStack>
                    </Box>
                    <Box>
                      <Text color="gray.300" fontSize="md" fontWeight="700" mb={2}>{beginnerMode ? 'Chips to start' : 'Starting stack'}</Text>
                      <HStack spacing={2} flexWrap="wrap">
                        {[100, 200, 500].map(s => <Choice key={s} active={startingStack === s} onClick={() => setStartingStack(s)}>{beginnerMode ? `$${s * 2}` : `${s} BB`}</Choice>)}
                      </HStack>
                    </Box>
                  </Grid>
                </VStack>
              )}
              <Box mt={3} pt={3} borderTop="1px solid" borderColor="whiteAlpha.200">
                <Button size="sm" variant="link" color="gray.300" fontSize="md" leftIcon={<Users size={16} />} rightIcon={showJoin ? <ChevronUp size={16} /> : <ChevronDown size={16} />} onClick={() => setShowJoin(v => !v)}>
                  Join a friend's practice table
                </Button>
                {showJoin && (
                  <HStack mt={3}>
                    <Input placeholder="Code from your friend" value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} maxLength={6}
                      textAlign="center" fontFamily="mono" fontSize="xl" letterSpacing="0.25em" minH="50px" bg="blackAlpha.400" border="1px solid" borderColor="whiteAlpha.300" color="white" />
                    <Button minH="50px" colorScheme="cyan" fontSize="md" isLoading={isJoining} isDisabled={!joinCode.trim()} onClick={handleJoinTable}>Join</Button>
                  </HStack>
                )}
              </Box>
              {error && <Text mt={3} color="#fca5a5" fontSize="md">{error}</Text>}
            </Box>

            {/* New player nudge */}
            {!poker101Done && (
              <Box as="button" textAlign="left" onClick={() => setShowPoker101(true)} bg="rgba(251,191,36,0.1)" border="1px solid rgba(251,191,36,0.5)" borderRadius="2xl" p={4}>
                <HStack spacing={3}>
                  <Box bg="rgba(251,191,36,0.2)" color={POKER_COLORS.gold} p={3} borderRadius="xl"><Star size={22} /></Box>
                  <Box flex={1}>
                    <Text color={POKER_COLORS.gold} fontWeight="800" fontSize="lg">New to poker? Start here</Text>
                    <Text color="gray.300" fontSize="md">A five-minute walk-through of the rules and hand rankings.</Text>
                  </Box>
                  <Box color={POKER_COLORS.gold}><ChevronRight size={22} /></Box>
                </HStack>
              </Box>
            )}

            {/* Activities */}
            <Box>
              <Text color="gray.400" fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" mb={2}>More ways to learn</Text>
              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
                <Tile icon={<Brain size={24} />} color={POKER_COLORS.gold} title="Practice a situation" tutorial="training-scenario"
                  text={beginnerMode ? 'Tough spots, one at a time, with instant feedback on your choice.' : 'Pot odds, bluffing and position drills with coach feedback.'}
                  onClick={() => navigate('/training/scenario')} />
                <Tile icon={<Grid3x3 size={24} />} color="#22d3ee" title="Preflop charts" tag="New"
                  text="Which hands to play from each seat. Tap a hand, see the answer." onClick={() => navigate('/training/charts')} />
                <Tile icon={<Trophy size={24} />} color="#f59e0b" title="Tournament challenges" tag="Earn coins"
                  text={beginnerMode ? 'Short games that teach tournament skills. Pass one, earn PSS Coins.' : 'Push/fold, ICM and final-table drills. Harder tiers pay more.'}
                  onClick={() => navigate('/training/tournament')} />
                <Tile icon={<Bot size={24} />} color={POKER_COLORS.cyan} title="Ask the coach" tutorial="training-coach"
                  text="Any poker question, answered in plain English. Paste a hand for a review." onClick={() => navigate('/training/coach')} />
                <Tile icon={<BookOpen size={24} />} color="#a78bfa" title="Poker words explained"
                  text="Every term in the app, in plain language, with examples." onClick={() => navigate('/training/glossary')} />
                <Tile icon={<GraduationCap size={24} />} color={POKER_COLORS.gold} title="Poker 101"
                  text={poker101Done ? 'Watch the basics again any time.' : 'The rules and hand rankings in five minutes.'} onClick={() => setShowPoker101(true)} />
              </Grid>
            </Box>

            {/* Progress strip */}
            <Box bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" p={4} data-tutorial="training-rewards">
              <Grid templateColumns="repeat(3, 1fr)" gap={2} mb={3}>
                <Box textAlign="center">
                  <HStack justify="center" spacing={1} color={POKER_COLORS.gold}><Coins size={16} /><Text fontSize="xs" color="gray.400">{coinsLabel}</Text></HStack>
                  <Text color={POKER_COLORS.gold} fontWeight="900" fontSize="xl">{coinsValue}</Text>
                </Box>
                <Box textAlign="center">
                  <HStack justify="center" spacing={1} color={skillProgress?.streakDays ? POKER_COLORS.gold : 'gray.500'}><Flame size={16} /><Text fontSize="xs" color="gray.400">Day streak</Text></HStack>
                  <Text color="white" fontWeight="900" fontSize="xl">{skillProgress?.streakDays || 0}</Text>
                </Box>
                <Box textAlign="center">
                  <HStack justify="center" spacing={1} color={POKER_COLORS.cyan}><Trophy size={16} /><Text fontSize="xs" color="gray.400">Level</Text></HStack>
                  <Text color="white" fontWeight="900" fontSize="xl" textTransform="capitalize">{String(skillProgress?.level || 'Beginner')}</Text>
                </Box>
              </Grid>
              <HStack justify="space-between" mb={1}>
                <Text color="gray.300" fontSize="sm">Next level</Text>
                <Text color="white" fontSize="sm" fontWeight="700">{skillProgress?.xp || 0} / {skillProgress?.xpToNextLevel || 1000} XP</Text>
              </HStack>
              <Progress value={xpPct} size="md" h="10px" borderRadius="full" bg="blackAlpha.400" sx={{ '& > div': { bg: `linear-gradient(90deg, ${POKER_COLORS.cyan}, ${POKER_COLORS.gold})` } }} />
              <Button mt={3} w="100%" minH="46px" variant="outline" colorScheme={canClaimDaily || canClaimWeekly ? 'yellow' : 'gray'} color={canClaimDaily || canClaimWeekly ? POKER_COLORS.gold : 'gray.300'} fontSize="md"
                leftIcon={<Gift size={18} />} rightIcon={showRewards ? <ChevronUp size={16} /> : <ChevronDown size={16} />} onClick={() => setShowRewards(v => !v)}>
                {canClaimDaily || canClaimWeekly ? 'Rewards ready to claim' : 'Daily challenges and rewards'}
              </Button>
              {showRewards && (
                <>
                  <Box mt={3}><DailyRewardsWidget variant="compact" isRestrictedState={isRestrictedState} /></Box>
                  <Text color="gray.500" fontSize="sm" mt={2}>Training coin rewards must be played through 3x before they can be redeemed.</Text>
                </>
              )}
            </Box>

            {/* Recent sessions */}
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text color="gray.400" fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em">Recent sessions</Text>
                {reports.length > 3 && <Button size="sm" variant="link" color={POKER_COLORS.cyan} fontSize="md" onClick={() => setView('history')}>See all {reports.length}</Button>}
              </HStack>
              {recent.length === 0 ? (
                <Box bg={CARD} border="1px dashed" borderColor={BORDER} borderRadius="2xl" p={5} textAlign="center">
                  <Text color="gray.300" fontSize="md">Your first practice report will show up here.</Text>
                </Box>
              ) : (
                <VStack align="stretch" spacing={2}>{recent.map(r => reportCard(r, true))}</VStack>
              )}
            </Box>
          </>
        )}

        {view === 'history' && (
          <VStack spacing={3} align="stretch">
            {retentionStatus && (retentionStatus.isAtLimit || retentionStatus.isNearLimit) && (
              <HStack bg={retentionStatus.isAtLimit ? 'rgba(248,113,113,0.12)' : 'rgba(251,191,36,0.12)'} border="1px solid" borderColor={retentionStatus.isAtLimit ? 'rgba(248,113,113,0.5)' : 'rgba(251,191,36,0.5)'} borderRadius="xl" p={3} align="flex-start">
                <Box color={retentionStatus.isAtLimit ? '#fca5a5' : POKER_COLORS.gold} mt={0.5}><AlertTriangle size={18} /></Box>
                <Text color="gray.200" fontSize="md">
                  {retentionStatus.isAtLimit ? 'Your saved sessions are full' : 'Saved sessions are almost full'} ({retentionStatus.totalReports} of {retentionStatus.maxReports}). {retentionStatus.recommendedAction || 'Delete old ones to make room.'}
                </Text>
              </HStack>
            )}
            <HStack justify="space-between" flexWrap="wrap" gap={2}>
              <Text color="white" fontWeight="800" fontSize="lg">{reports.length} session{reports.length === 1 ? '' : 's'}</Text>
              {reports.length > 0 && (
                selectMode ? (
                  <HStack spacing={2}>
                    <Button size="sm" minH="40px" variant="outline" colorScheme="gray" color="gray.200" onClick={() => setSelectedReports(new Set(reports.map(r => r.reportId)))}>Select all</Button>
                    <Button size="sm" minH="40px" colorScheme="red" leftIcon={<Trash2 size={14} />} isDisabled={selectedReports.size === 0} isLoading={isDeleting} onClick={onDeleteOpen}>Delete {selectedReports.size || ''}</Button>
                    <Button size="sm" minH="40px" variant="ghost" color="gray.300" onClick={() => { setSelectMode(false); setSelectedReports(new Set()); }}>Done</Button>
                  </HStack>
                ) : (
                  <HStack spacing={2}>
                    <Button size="sm" minH="40px" variant="outline" colorScheme="cyan" leftIcon={<MessageCircle size={14} />} onClick={() => navigate('/training/coach')}>Review with coach</Button>
                    <Button size="sm" minH="40px" variant="ghost" color="gray.300" leftIcon={<Trash2 size={14} />} onClick={() => setSelectMode(true)}>Tidy up</Button>
                  </HStack>
                )
              )}
            </HStack>
            {reports.length === 0 ? (
              <Box bg={CARD} border="1px dashed" borderColor={BORDER} borderRadius="2xl" p={6} textAlign="center">
                <Text color="gray.300" fontSize="md" mb={3}>No practice sessions yet.</Text>
                <Button minH="46px" colorScheme="cyan" fontSize="md" onClick={() => setView('home')}>Play your first hand</Button>
              </Box>
            ) : (
              <>
                <VStack align="stretch" spacing={2}>{reports.slice(0, historyShown).map(r => reportCard(r))}</VStack>
                {reports.length > historyShown && (
                  <Button minH="46px" variant="outline" colorScheme="gray" color="gray.200" fontSize="md" onClick={() => setHistoryShown(n => n + HISTORY_PAGE)}>
                    Show {Math.min(HISTORY_PAGE, reports.length - historyShown)} more
                  </Button>
                )}
              </>
            )}
          </VStack>
        )}

        {view === 'progress' && (
          <VStack spacing={4} align="stretch" data-tutorial="training-progress">
            {!progress ? (
              <Box bg={CARD} border="1px dashed" borderColor={BORDER} borderRadius="2xl" p={6} textAlign="center">
                <Box color={POKER_COLORS.cyan} mb={2}><TrendingUp size={36} style={{ margin: '0 auto' }} /></Box>
                <Text color="gray.300" fontSize="md">Play a few practice hands and your progress shows up here.</Text>
              </Box>
            ) : (
              <>
                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                  {[
                    ['Games played', String(progress.totalSessions), 'white'],
                    ['Hands played', String((progress as any).totalHands ?? progress.totalHandsPlayed ?? 0), 'white'],
                    ['Average score', (progress.averageScore || 0).toFixed(1), scoreColor(progress.averageScore || 0)],
                    ['Best score', (progress.bestScore || 0).toFixed(1), scoreColor(progress.bestScore || 0)],
                  ].map(([label, value, color]) => (
                    <Box key={label} bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" p={4} textAlign="center">
                      <Text fontSize="3xl" fontWeight="900" color={color as string} lineHeight={1}>{value}</Text>
                      <Text color="gray.400" fontSize="md" mt={1}>{label}</Text>
                    </Box>
                  ))}
                </Grid>

                {progress.recentScores && progress.recentScores.length > 1 && (
                  <Box bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" p={4}>
                    <Text color="white" fontWeight="800" fontSize="lg" mb={3}>Your last {progress.recentScores.length} scores</Text>
                    <HStack align="end" justify="space-between" h="110px" spacing={2}>
                      {progress.recentScores.map((score, idx) => (
                        <VStack key={idx} flex={1} spacing={1}>
                          <Box w="100%" bg={scoreColor(score)} borderRadius="md" h={`${Math.max(6, (score / 10) * 90)}px`} />
                          <Text color="gray.400" fontSize="xs">{score.toFixed(1)}</Text>
                        </VStack>
                      ))}
                    </HStack>
                    {progress.scoreImprovement !== undefined && progress.scoreImprovement !== 0 && (
                      <Text textAlign="center" mt={3} fontSize="md" color={progress.scoreImprovement > 0 ? '#4ade80' : '#fca5a5'}>
                        {progress.scoreImprovement > 0 ? 'Up' : 'Down'} {Math.abs(progress.scoreImprovement).toFixed(1)}% over time{progress.scoreImprovement > 0 ? '. Keep going.' : '. Try a scenario drill.'}
                      </Text>
                    )}
                  </Box>
                )}

                {progress.metricAverages && Object.keys(progress.metricAverages).length > 0 && (
                  <Box bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" p={4}>
                    <Text color="white" fontWeight="800" fontSize="lg" mb={3}>Your skills</Text>
                    <VStack spacing={4} align="stretch">
                      {Object.entries(progress.metricAverages).map(([name, avg]) => {
                        const label: Record<string, string> = beginnerMode ? {
                          preflop_discipline: 'Choosing starting hands', position_awareness: 'Using your seat', postflop_aggression: 'Betting decisions', bluffing: 'Bluffing', pot_odds: 'The math',
                        } : {
                          preflop_discipline: 'Preflop discipline', position_awareness: 'Position awareness', postflop_aggression: 'Post-flop aggression', bluffing: 'Bluffing strategy', pot_odds: 'Pot odds and draws',
                        };
                        const v = avg as number;
                        return (
                          <Box key={name}>
                            <HStack justify="space-between" mb={1}>
                              <Text color="gray.200" fontSize="md">{label[name] || name}</Text>
                              <Text color={scoreColor(v)} fontWeight="800" fontSize="md">{v.toFixed(1)}/10</Text>
                            </HStack>
                            <Progress value={v * 10} h="10px" borderRadius="full" bg="blackAlpha.400" sx={{ '& > div': { background: scoreColor(v) } }} />
                          </Box>
                        );
                      })}
                    </VStack>
                  </Box>
                )}

                {progress.milestones && progress.milestones.length > 0 && (
                  <Box bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" p={4}>
                    <HStack mb={3} color={POKER_COLORS.gold}><Trophy size={20} /><Text color="white" fontWeight="800" fontSize="lg">Achievements</Text></HStack>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      {progress.milestones.map((m: any) => (
                        <Box key={m.id} p={3} bg="rgba(251,191,36,0.08)" border="1px solid rgba(251,191,36,0.25)" borderRadius="xl">
                          <Text color="white" fontWeight="700" fontSize="md">{m.name}</Text>
                          <Text color="gray.400" fontSize="sm">{m.description}</Text>
                        </Box>
                      ))}
                    </Grid>
                  </Box>
                )}

                {analytics?.recommendedFocus?.length > 0 && (
                  <Box bg={CARD} border="1px solid" borderColor={`${POKER_COLORS.cyan}55`} borderRadius="2xl" p={4}>
                    <HStack mb={3} color={POKER_COLORS.cyan}><Target size={20} /><Text color="white" fontWeight="800" fontSize="lg">What to work on next</Text></HStack>
                    <VStack align="stretch" spacing={2} mb={4}>
                      {analytics.recommendedFocus.map((rec: string, idx: number) => (
                        <HStack key={idx} align="flex-start"><Text color="#4ade80" fontSize="md">•</Text><Text color="gray.200" fontSize="md">{rec}</Text></HStack>
                      ))}
                    </VStack>
                    <Button w="100%" minH="50px" colorScheme="purple" fontSize="md" leftIcon={<Brain size={18} />} onClick={() => navigate('/training/scenario')}>Practice these</Button>
                  </Box>
                )}
              </>
            )}
          </VStack>
        )}

        {/* Delete dialog */}
        <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose} isCentered>
          <AlertDialogOverlay>
            <AlertDialogContent bg="gray.800" borderColor="red.600" borderWidth={1} mx={4}>
              <AlertDialogHeader fontSize="lg" fontWeight="bold" color="white">Delete sessions</AlertDialogHeader>
              <AlertDialogBody color="gray.300" fontSize="md">Delete {selectedReports.size} saved session{selectedReports.size === 1 ? '' : 's'}? This cannot be undone.</AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} variant="ghost" color="gray.300" onClick={onDeleteClose}>Cancel</Button>
                <Button colorScheme="red" onClick={handleDeleteSelected} ml={3} isLoading={isDeleting}>Delete</Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </VStack>
    </Container>
  );
};

export default TrainingLobby;
